<template>
  <IntercomLayout>
    <!-- Topbar -->
    <template #topbar>
      <GymTopbar
        :business-name="businessName"
        :user="user"
        :global-user="globalUser"
        :search-fn="globalSearch"
        @select-member="handleSelectMember"
        @logout="handleLogout"
      />
    </template>

    <!-- Sidebar -->
    <template #sidebar>
      <IntercomSidebar :nav-sections="navSections" />
    </template>

    <!-- Main Content -->
    <div class="settings-view">
      <!-- Page Header -->
      <div class="page-header">
        <h1 class="page-title">{{ $t('settings.title') }}</h1>
        <span class="account-id">Account ID: {{ businessId }}</span>
      </div>

      <!-- Loading State -->
      <div v-if="loading" class="loading-state">
        <IntercomSpinner />
      </div>

      <template v-else>
        <!-- Tabs -->
        <div class="settings-tabs">
          <button
            v-for="tab in tabs"
            :key="tab.key"
            class="tab-button"
            :class="{ active: activeTab === tab.key }"
            @click="activeTab = tab.key"
          >
            {{ $t(tab.label) }}
          </button>
        </div>

        <!-- Tab Content -->
        <IntercomCard class="settings-content">
          <!-- Business Settings -->
          <BusinessSettings v-if="activeTab === 'business'" />

          <!-- Membership Settings -->
          <div v-if="activeTab === 'membership'" class="settings-section">
            <h3 class="section-title">{{ $t('settings.membership.title') }}</h3>

            <div class="form-grid">
              <div class="form-field">
                <IntercomInput
                  v-model.number="settings.grace_period_days"
                  :label="$t('settings.membership.gracePeriod')"
                  type="number"
                  min="0"
                />
                <span class="field-help">{{ $t('settings.membership.gracePeriodHelp') }}</span>
              </div>

              <div class="form-field">
                <IntercomInput
                  v-model.number="settings.reminder_days_before"
                  :label="$t('settings.membership.reminderDays')"
                  type="number"
                  min="1"
                />
                <span class="field-help">{{ $t('settings.membership.reminderDaysHelp') }}</span>
              </div>

              <div class="form-field">
                <IntercomInput
                  v-model="settings.opening_time"
                  :label="$t('settings.membership.openingTime')"
                  type="time"
                />
              </div>

              <div class="form-field">
                <IntercomInput
                  v-model="settings.closing_time"
                  :label="$t('settings.membership.closingTime')"
                  type="time"
                />
              </div>

              <div class="form-field">
                <IntercomInput
                  v-model="settings.member_prefix"
                  :label="$t('settings.membership.memberPrefix')"
                  maxlength="10"
                />
                <span class="field-help">{{ $t('settings.membership.memberPrefixHelp') }}</span>
              </div>

              <div class="form-field checkbox-field">
                <label class="checkbox-label">
                  <input type="checkbox" v-model="settings.send_birthday_wishes" />
                  <span>{{ $t('settings.membership.sendBirthdayWishes') }}</span>
                </label>
              </div>
            </div>
          </div>

          <!-- Receipt Settings -->
          <div v-if="activeTab === 'receipts'" class="settings-section">
            <h3 class="section-title">{{ $t('settings.receipts.title') }}</h3>

            <div class="form-grid">
              <div class="form-field">
                <IntercomInput
                  v-model="settings.receipt_prefix"
                  :label="$t('settings.receipts.receiptPrefix')"
                  maxlength="10"
                />
                <span class="field-help">{{ $t('settings.receipts.receiptPrefixHelp') }}</span>
              </div>

              <div class="form-field checkbox-field">
                <label class="checkbox-label">
                  <input type="checkbox" v-model="settings.show_gym_logo_on_receipt" />
                  <span>{{ $t('settings.receipts.showLogo') }}</span>
                </label>
              </div>

              <div class="form-field full-width">
                <label class="field-label">{{ $t('settings.receipts.footerText') }}</label>
                <textarea
                  v-model="settings.receipt_footer_text"
                  :placeholder="$t('settings.receipts.footerTextPlaceholder')"
                  class="text-input"
                  rows="2"
                ></textarea>
              </div>
            </div>
          </div>

          <!-- Notification Settings -->
          <div v-if="activeTab === 'notifications'" class="settings-section">
            <h3 class="section-title">{{ $t('settings.notifications.title') }}</h3>

            <div class="form-grid">
              <!-- WhatsApp Master Toggle -->
              <div class="form-field checkbox-field full-width whatsapp-toggle">
                <label class="checkbox-label">
                  <input type="checkbox" v-model="messagingSettings.whatsapp_enabled" />
                  <span>{{ $t('settings.notifications.whatsappEnabled') }}</span>
                </label>
                <span class="field-help">{{ $t('settings.notifications.whatsappEnabledHelp') }}</span>
              </div>

              <div class="form-field checkbox-field">
                <label class="checkbox-label">
                  <input type="checkbox" v-model="settings.auto_send_receipt" />
                  <span>{{ $t('settings.notifications.autoSendReceipt') }}</span>
                </label>
              </div>

              <div class="form-field checkbox-field">
                <label class="checkbox-label">
                  <input type="checkbox" v-model="settings.auto_send_reminder" />
                  <span>{{ $t('settings.notifications.autoSendReminder') }}</span>
                </label>
              </div>

              <div class="form-field full-width">
                <label class="field-label">{{ $t('settings.notifications.welcomeMessage') }}</label>
                <textarea
                  v-model="settings.whatsapp_welcome_msg"
                  class="text-input"
                  rows="4"
                ></textarea>
              </div>

              <div class="form-field full-width">
                <label class="field-label">{{ $t('settings.notifications.renewalMessage') }}</label>
                <textarea
                  v-model="settings.whatsapp_renewal_msg"
                  class="text-input"
                  rows="4"
                ></textarea>
              </div>
            </div>
          </div>

          <!-- Success/Error Messages -->
          <div v-if="successMessage" class="success-message">
            {{ successMessage }}
          </div>
          <div v-if="errorMessage" class="error-message">
            {{ errorMessage }}
          </div>

          <!-- Actions - only for non-business tabs (BusinessSettings has its own save) -->
          <div class="settings-actions" v-if="activeTab !== 'business'">
            <IntercomButton variant="secondary" @click="fetchSettings">
              {{ $t('common.cancel') }}
            </IntercomButton>
            <IntercomButton
              variant="primary"
              :loading="saving"
              :loading-text="$t('settings.saving')"
              @click="handleSave"
            >
              {{ $t('settings.saveChanges') }}
            </IntercomButton>
          </div>
        </IntercomCard>
      </template>
    </div>
  </IntercomLayout>
</template>

<script setup>
import { ref, reactive, computed, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { useAuth } from '@shared/composables/useAuth.js'
import { useGym } from '../composables/useGym.js'
import { useNavigation } from '../composables/useNavigation.js'
import { IntercomLayout, IntercomSidebar, IntercomCard, IntercomButton, IntercomInput, IntercomSpinner } from '@shared/components/intercom'
import GymTopbar from '../components/GymTopbar.vue'
import BusinessSettings from '../components/settings/BusinessSettings.vue'

const router = useRouter()
const { t } = useI18n()
const { user, globalUser, logout } = useAuth()
const { getSettings, updateSettings, getMessagingSettings, updateMessagingSettings, globalSearch } = useGym()

const { navSections } = useNavigation()

// Tabs configuration
const tabs = [
  { key: 'business', label: 'settings.tabs.business' },
  { key: 'membership', label: 'settings.tabs.membership' },
  { key: 'receipts', label: 'settings.tabs.receipts' },
  { key: 'notifications', label: 'settings.tabs.notifications' },
]

// State
const loading = ref(true)
const saving = ref(false)
const activeTab = ref('business')
const successMessage = ref('')
const errorMessage = ref('')

const settings = reactive({
  // Membership
  grace_period_days: 7,
  reminder_days_before: 3,
  send_birthday_wishes: true,
  opening_time: '05:00',
  closing_time: '22:00',
  member_prefix: 'GYM',

  // Receipts
  receipt_prefix: 'RCP',
  show_gym_logo_on_receipt: true,
  receipt_footer_text: '',

  // Notifications
  auto_send_receipt: true,
  auto_send_reminder: true,
  whatsapp_welcome_msg: '',
  whatsapp_renewal_msg: '',
})

// Messaging settings (shared, from messaging_settings table)
const messagingSettings = reactive({
  whatsapp_enabled: false,
})

// Computed
const businessName = computed(() => user.value?.business?.name || '')
const businessId = computed(() => user.value?.business_id || user.value?.business?.id || null)

// Methods
const fetchSettings = async () => {
  try {
    loading.value = true
    errorMessage.value = ''

    // Fetch gym settings and messaging settings in parallel
    const [gymResponse, msgResponse] = await Promise.all([
      getSettings(),
      getMessagingSettings().catch(() => ({})), // Graceful fallback (already unwrapped format)
    ])

    const data = gymResponse.settings || {}

    // Map gym settings to form
    settings.grace_period_days = data.grace_period_days ?? 7
    settings.reminder_days_before = data.reminder_days_before ?? 3
    settings.send_birthday_wishes = data.send_birthday_wishes ?? true
    settings.opening_time = data.opening_time?.slice(0, 5) || '05:00'
    settings.closing_time = data.closing_time?.slice(0, 5) || '22:00'
    settings.member_prefix = data.member_prefix || 'GYM'

    settings.receipt_prefix = data.receipt_prefix || 'RCP'
    settings.show_gym_logo_on_receipt = data.show_gym_logo_on_receipt ?? true
    settings.receipt_footer_text = data.receipt_footer_text || ''

    settings.auto_send_receipt = data.auto_send_receipt ?? true
    settings.auto_send_reminder = data.auto_send_reminder ?? true
    settings.whatsapp_welcome_msg = data.whatsapp_welcome_msg || ''
    settings.whatsapp_renewal_msg = data.whatsapp_renewal_msg || ''

    // Map messaging settings (msgResponse is already unwrapped by apiClient)
    messagingSettings.whatsapp_enabled = msgResponse.whatsapp_enabled ?? false
  } catch (error) {
    console.error('Failed to fetch settings:', error)
    errorMessage.value = t('errors.generic')
  } finally {
    loading.value = false
  }
}

const handleSave = async () => {
  try {
    saving.value = true
    successMessage.value = ''
    errorMessage.value = ''

    // Format times for API
    const data = {
      ...settings,
      opening_time: settings.opening_time + ':00',
      closing_time: settings.closing_time + ':00',
    }

    // Save gym settings and messaging settings in parallel
    await Promise.all([
      updateSettings(data),
      updateMessagingSettings({
        whatsapp_enabled: messagingSettings.whatsapp_enabled,
      }),
    ])

    successMessage.value = t('settings.saved')

    // Clear success message after 3 seconds
    setTimeout(() => {
      successMessage.value = ''
    }, 3000)
  } catch (error) {
    console.error('Failed to save settings:', error)
    errorMessage.value = error.message || t('errors.generic')
  } finally {
    saving.value = false
  }
}

const handleSelectMember = (member) => {
  router.push(`/members/${member.id}`)
}

const handleLogout = async () => {
  await logout()
  router.push('/login')
}

onMounted(() => {
  fetchSettings()
})
</script>

<style scoped>
@import '@shared/styles/intercom-tokens.css';

.settings-view {
  padding: var(--space-6);
  max-width: 900px;
}

.page-header {
  margin-bottom: var(--space-6);
}

.page-title {
  font-size: var(--font-size-2xl);
  font-weight: var(--font-weight-bold);
  color: var(--color-charcoal);
  margin: 0;
}

.account-id {
  display: inline-block;
  font-size: 12px;
  color: var(--text-tertiary, #98a2b3);
  background: var(--bg-secondary, #f9fafb);
  padding: 2px 8px;
  border-radius: 4px;
  font-family: monospace;
  margin-top: 4px;
}

.loading-state {
  display: flex;
  justify-content: center;
  padding: var(--space-12);
}

.settings-tabs {
  display: flex;
  gap: var(--space-1);
  background: var(--color-gray-100);
  padding: var(--space-1);
  border-radius: var(--radius-md);
  margin-bottom: var(--space-6);
}

.tab-button {
  flex: 1;
  padding: var(--space-2) var(--space-4);
  border: none;
  background: transparent;
  border-radius: var(--radius-sm);
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-medium);
  color: var(--color-gray-600);
  cursor: pointer;
  transition: all 0.2s;
}

.tab-button:hover {
  color: var(--color-charcoal);
}

.tab-button.active {
  background: var(--color-white);
  color: var(--color-charcoal);
  box-shadow: var(--shadow-sm);
}

.settings-content {
  padding: var(--space-6);
}

.settings-section {
  margin-bottom: var(--space-6);
}

.section-title {
  font-size: var(--font-size-lg);
  font-weight: var(--font-weight-semibold);
  color: var(--color-charcoal);
  margin: 0 0 var(--space-5) 0;
  padding-bottom: var(--space-3);
  border-bottom: 1px solid var(--color-gray-200);
}

.form-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: var(--space-5);
}

.form-field {
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
}

.form-field.full-width {
  grid-column: span 2;
}

.form-field.checkbox-field {
  justify-content: flex-end;
}

.field-label {
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-medium);
  color: var(--color-charcoal);
}

.field-help {
  font-size: var(--font-size-xs);
  color: var(--color-gray-500);
}

.text-input {
  width: 100%;
  padding: var(--space-3);
  border: 1px solid var(--color-gray-300);
  border-radius: var(--radius-md);
  font-size: var(--font-size-base);
  font-family: inherit;
  resize: vertical;
}

.text-input:focus {
  outline: none;
  border-color: var(--color-blue);
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

.checkbox-label {
  display: flex;
  align-items: center;
  gap: var(--space-2);
  cursor: pointer;
  font-size: var(--font-size-base);
  color: var(--color-charcoal);
}

.checkbox-label input {
  width: 18px;
  height: 18px;
  cursor: pointer;
}

.whatsapp-toggle {
  background: var(--color-gray-50);
  padding: var(--space-4);
  border-radius: var(--radius-md);
  border: 1px solid var(--color-gray-200);
  margin-bottom: var(--space-2);
}

.whatsapp-toggle .checkbox-label {
  font-weight: var(--font-weight-medium);
}

.whatsapp-toggle .field-help {
  margin-top: var(--space-1);
  display: block;
}

.success-message {
  padding: var(--space-3) var(--space-4);
  background: var(--color-success-bg);
  border: 1px solid var(--color-success);
  border-radius: var(--radius-md);
  color: var(--color-success);
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-medium);
  margin-bottom: var(--space-4);
}

.error-message {
  padding: var(--space-3) var(--space-4);
  background: var(--color-error-bg);
  border: 1px solid var(--color-error);
  border-radius: var(--radius-md);
  color: var(--color-error);
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-medium);
  margin-bottom: var(--space-4);
}

.settings-actions {
  display: flex;
  justify-content: flex-end;
  gap: var(--space-3);
  padding-top: var(--space-6);
  border-top: 1px solid var(--color-gray-200);
}

@media (max-width: 768px) {
  .settings-view {
    padding: var(--space-4);
  }

  .settings-tabs {
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
  }

  .tab-button {
    white-space: nowrap;
  }

  .form-grid {
    grid-template-columns: 1fr;
  }

  .form-field.full-width {
    grid-column: span 1;
  }
}
</style>
